/**
 * Settings — Appearance (mode · density · graph contrast) for everyone, role
 * assignment for admins (doc 147 §8.2 "Role assignment", §8.9.5 density,
 * §8.9.6 revised for theming).
 *
 * `mode` is the persisted tri-state {auto, dark, light}; `auto` defers to the
 * OS preference. `graphContrast` swaps the constellation token bundle between
 * AA (Standard) and AAA-grade (High) contrast — see `theme/palette.test.ts`.
 */
import { useState } from 'react';
import type { InternalUser } from '../state/store.js';
import type { DensityMode } from '../theme/cssVariables.js';
import type { GraphContrast } from '../theme/tokens.js';
import type { ModeSetting } from '../state/appearance.js';
import { EmptyState } from '../components/EmptyState.js';

const DENSITIES: DensityMode[] = ['compact', 'regular', 'expanded'];
const MODES: { value: ModeSetting; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];
const CONTRASTS: { value: GraphContrast; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'high', label: 'High contrast' },
];
const ROLES: InternalUser['role'][] = ['engineer', 'designer', 'admin'];

export interface SettingsViewProps {
  user: InternalUser;
  density: DensityMode;
  mode: ModeSetting;
  graphContrast: GraphContrast;
  onDensityChange: (mode: DensityMode) => void;
  onModeChange: (mode: ModeSetting) => void;
  onGraphContrastChange: (level: GraphContrast) => void;
  /** Admin-only — assign a role; rejects on failure. */
  onSetUserRole: (uid: string, role: InternalUser['role']) => Promise<void>;
}

export function SettingsView({
  user,
  density,
  mode,
  graphContrast,
  onDensityChange,
  onModeChange,
  onGraphContrastChange,
  onSetUserRole,
}: SettingsViewProps): React.JSX.Element {
  const [targetUid, setTargetUid] = useState('');
  const [targetRole, setTargetRole] = useState<InternalUser['role']>('engineer');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleAssign = async (): Promise<void> => {
    if (!targetUid.trim()) {
      setStatus('Enter a target user id.');
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await onSetUserRole(targetUid.trim(), targetRole);
      setStatus(`Set ${targetUid.trim()} → ${targetRole}.`);
      setTargetUid('');
    } catch {
      setStatus('Role assignment failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article>
      <h1 className="entity-title">Settings</h1>

      <section style={{ margin: 'var(--sp-lg) 0' }}>
        <div className="section-label">Appearance</div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 'var(--sp-md)',
            rowGap: 'var(--sp-sm)',
            alignItems: 'center',
            marginTop: 'var(--sp-sm)',
          }}
        >
          <span style={{ color: 'var(--text2)', fontSize: 'var(--fs-sm)' }}>Mode</span>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
            {MODES.map((m) => (
              <button
                key={m.value}
                className={`btn ${m.value === mode ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={m.value === mode}
                onClick={() => onModeChange(m.value)}
              >
                {m.label}
              </button>
            ))}
          </div>

          <span style={{ color: 'var(--text2)', fontSize: 'var(--fs-sm)' }}>Density</span>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
            {DENSITIES.map((d) => (
              <button
                key={d}
                className={`btn ${d === density ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={d === density}
                onClick={() => onDensityChange(d)}
              >
                {d}
              </button>
            ))}
          </div>

          <span style={{ color: 'var(--text2)', fontSize: 'var(--fs-sm)' }}>Graph contrast</span>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
            {CONTRASTS.map((c) => (
              <button
                key={c.value}
                className={`btn ${c.value === graphContrast ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={c.value === graphContrast}
                onClick={() => onGraphContrastChange(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <p
          style={{
            color: 'var(--text3)',
            fontSize: 'var(--fs-sm)',
            marginTop: 'var(--sp-md)',
            maxWidth: '60ch',
          }}
        >
          High contrast brightens constellation edges and adds a 1px outline to every node — for
          low-vision use or projection on bright displays.
        </p>
      </section>

      <section>
        <div className="section-label">Role assignment</div>
        {user.role !== 'admin' ? (
          <EmptyState
            icon="lock"
            title="Admin only"
            detail="Role assignment is restricted to admins."
          />
        ) : (
          <div
            className="card"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-sm)' }}
          >
            <input
              className="palette-input"
              style={{ borderBottom: '1px solid var(--border-s)', borderRadius: 'var(--r-sm)' }}
              placeholder="Target user uid"
              value={targetUid}
              onChange={(e) => setTargetUid(e.target.value)}
              aria-label="Target user uid"
            />
            <select
              aria-label="Role"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value as InternalUser['role'])}
              style={{
                padding: 'var(--sp-sm)',
                background: 'var(--s2)',
                color: 'var(--text)',
                border: '1px solid var(--border-s)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={handleAssign} disabled={busy}>
              {busy ? 'Assigning…' : 'Assign role'}
            </button>
            {status && (
              <p role="status" style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)' }}>
                {status}
              </p>
            )}
          </div>
        )}
      </section>
    </article>
  );
}
